import {
  AssignmentModel,
  AssignmentStatusEnum,
  CaseExtendedModel,
  ExtendedPosition,
} from 'api/model';
import { useAssignmentSyncSpeedLimitDataHook } from 'api/assignment/assignment';
import Button from 'components/inputs/Button';
import Select from 'components/inputs/Select';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { RequestStatus, useApiCallFn } from 'hooks/useApiCallFn';
import TransparentSpinner from 'components/spinners/TransparentSpinner';
import { FixedPosition } from 'components/DetailedCase/DetailedCaseFormProvider/types';
import { formatTime } from 'utils/date-helpers';

const hasSpeedLimitData = (p: ExtendedPosition) => {
  // Positions without speed data arrive with speedLimitTimestamp = null, so
  // a strict undefined check would count them as having data (hiding the
  // "Synka med vägdata" button).
  return !!p.speedLimitTimestamp;
};

const AssignmentAddressesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
  border: 1px solid #ccc;
  padding: 5px;
`;

const Horizontal = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;
const SubTitle = styled.h2`
  font-size: 18px;
`;

const FiltrationWindow = styled.div`
  display: flex;
  flex-direction: column;
  width: 300px;
  gap: 6px;
  margin-right: 6px;
`;

const Spacer = styled.div`
  flex: 1;
`;

const MyButton = styled(Button)`
  width: 225px;
  padding-top: 10px;
`;

type MapControlProps = {
  caseResponse?: CaseExtendedModel;
  positions: ExtendedPosition[];
  onFilter?: (
    assignmentId?: number,
    fromTimestamp?: Date,
    toTimestamp?: Date,
    isLive?: boolean
  ) => void;
  fixedPositions?: FixedPosition[] | undefined;
};

const MapControls: FC<MapControlProps> = ({
  caseResponse,
  onFilter,
  positions,
  fixedPositions,
}) => {
  const theme = useTheme();
  const [assignmentId, setAssignmentId] = useState<number>(
    caseResponse?.case.assignmentList.length === 1
      ? caseResponse?.case.assignmentList[0].assignmentID
      : -1
  );
  const [isLive, setIsLive] = useState<boolean>(true);

  const syncSpeedLimitData = useAssignmentSyncSpeedLimitDataHook();
  const syncSpeedDataCall = useApiCallFn((_assignmentId: number) =>
    syncSpeedLimitData({ assignmentId: _assignmentId })
  );

  /**
   * Returns true if there are at least 10 positions in a row that lack speed limit data
   * @param positions
   * @returns boolean
   */
  const hasManyPositionsWithoutSpeedLimitData = useMemo(() => {
    let count = 0;
    for (let i = 0; i < positions.length; i += 1) {
      if (!hasSpeedLimitData(positions[i])) {
        count += 1;
      } else {
        count = 0;
      }
      if (count >= 10) {
        return true;
      }
    }
    return false;
  }, [positions]);

  const hasFinished = useMemo(() => {
    caseResponse?.case.assignmentList.some((a) => {
      if (assignmentId) {
        return (
          a.assignmentID === assignmentId &&
          a.assignmentStatusID >= AssignmentStatusEnum.Complete
        );
      }
      return a.assignmentStatusID >= AssignmentStatusEnum.Complete;
    });
    return true;
  }, [assignmentId, caseResponse?.case.assignmentList]);

  const handleUpdate = useCallback(() => {
    if (onFilter) {
      onFilter(assignmentId === -1 ? undefined : assignmentId);
    }
  }, [onFilter, assignmentId]);

  useEffect(() => {
    handleUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId, isLive, hasFinished]);

  const handleAssignmentIdChange = (value: string) => {
    setAssignmentId(Number(value));
  };

  const handleCheckSpeedLimits = async () => {
    if (assignmentId !== -1) {
      // run sync speed limit data
      await syncSpeedDataCall.run(assignmentId);
      // refresh map
      handleUpdate();
    }
  };

  const toggleLiveFeed = () => {
    setIsLive((p) => !p);
  };

  const getEnterAndExitTimesForAssignmentAndAddress = (
    assignment: AssignmentModel
  ): React.ReactNode => {
    let arrivalTime: Date | undefined;
    let departureTime: Date | undefined;
    // flatten fixedPositions to find the first matching entry
    const matchingFromPosition = fixedPositions?.find(
      (e) =>
        e.name === assignment.fromName && e.address === assignment.fromAddress
    );

    if (matchingFromPosition) {
      arrivalTime = matchingFromPosition.enterAndExitData?.find(
        (e) => e.assignmentId === assignment.assignmentID
      )?.enterTimestamp;
    }

    const matchingToPosition = fixedPositions?.find(
      (e) => e.name === assignment.toName && e.address === assignment.toAddress
    );
    if (matchingToPosition) {
      departureTime = matchingToPosition.enterAndExitData?.find(
        (e) => e.assignmentId === assignment.assignmentID
      )?.exitTimestamp;
    }

    if (!arrivalTime && !departureTime) {
      return null;
    }
    return (
      <span style={{ color: theme.colors.foreground.tint }}>
        Anlänt: {arrivalTime ? formatTime(arrivalTime) : '-'} Avfärd:{' '}
        {departureTime ? formatTime(departureTime) : '-'}
      </span>
    );
  };

  const getAssignmentAddressDetails = (
    assignment?: CaseExtendedModel['case']['assignmentList'][number]
  ) => {
    if (!assignment) {
      return null;
    }
    return (
      <AssignmentAddressesWrapper key={assignment.assignmentID}>
        <SubTitle>Uppdrag: {assignment.assignmentID}</SubTitle>
        <section>
          <span>
            Fälttestare: {assignment.assignedTo?.name || 'Ej tilldelad'}
          </span>
        </section>
        <section>
          <strong>Från adress:</strong>
          <br />
          {assignment.fromName}
          <br />
          {assignment.fromAddress}
          <br />
          {assignment.fromZip} {assignment.fromCity}
          <br />
          {getEnterAndExitTimesForAssignmentAndAddress(assignment)}
        </section>
        <section>
          <strong>Till adress:</strong>
          <br />
          {assignment.toName}
          <br />
          {assignment.toAddress}
          <br />
          {assignment.toZip} {assignment.toCity}
        </section>
      </AssignmentAddressesWrapper>
    );
  };

  return (
    <FiltrationWindow>
      {(caseResponse?.case.assignmentList.length ?? 0) > 1 && (
        <>
          <span>Uppdrag</span>
          <Select
            onChange={(e) => handleAssignmentIdChange(e.currentTarget.value)}
            value={assignmentId}
            width={225}
          >
            <option key="-1" value={-1}>
              Alla
            </option>
            {caseResponse?.case.assignmentList.map((a) => (
              <option key={a.assignmentID} value={a.assignmentID}>
                {a.fromAddress} - {a.toAddress} ({a.assignedTo?.name})
              </option>
            ))}
          </Select>
        </>
      )}
      {
        // show details for selected assignment, or for all assignments if -1 is selected
        assignmentId === -1
          ? caseResponse?.case.assignmentList.map((a) =>
              getAssignmentAddressDetails(a)
            )
          : getAssignmentAddressDetails(
              caseResponse?.case.assignmentList.find(
                (a) => a.assignmentID === assignmentId
              )
            )
      }

      {assignmentId !== -1 &&
        hasManyPositionsWithoutSpeedLimitData &&
        hasFinished && (
          <MyButton
            disabled={syncSpeedDataCall.status === RequestStatus.Fetching}
            onClick={handleCheckSpeedLimits}
          >
            <Horizontal>
              Synka med vägdata
              {syncSpeedDataCall.status === RequestStatus.Fetching && (
                <TransparentSpinner small />
              )}
            </Horizontal>
          </MyButton>
        )}
      <Spacer />
      {!hasFinished && (
        <MyButton onClick={toggleLiveFeed}>
          {isLive ? 'Pausa live-feed' : 'Starta live-feed'}
        </MyButton>
      )}
    </FiltrationWindow>
  );
};

export default MapControls;
